import { FilterEditActions, FilterEditActionTypes, InitFilterForm } from '@app/pcs/actions/filter-edit';
import { Filter } from '@app/pcs/models';
import { requiredValueValidator } from '@app/shared/validators';
import { Action } from '@ngrx/store';
import { createHandler, createReducer, executeReducers } from '@util';
import { LocalDateTime } from '@util/local-date-time';
import {
  box,
  Boxed,
  createFormGroupState,
  FormGroupState,
  formStateReducer,
  reset,
  setValue,
  updateGroup,
  validate
} from 'ngrx-forms';
import * as R from 'ramda';

export interface FilterForm {
  id: number;
  name: string;
  validFrom: Boxed<LocalDateTime>;
  validTo: Boxed<LocalDateTime>;
}

export interface State {
  form: FormGroupState<FilterForm>;
}

const FILTER_EDIT_FORM_ID = 'FilterForm';

type BoxProperties = (filter: Filter) => FilterForm;
const boxProperties = <BoxProperties>R.evolve({
  validFrom: box,
  validTo: box
});

const initialFormValue: Filter = {
  id: null,
  name: null,
  validFrom: null,
  validTo: null
};

const initialFormState: FormGroupState<FilterForm> =
  createFormGroupState<FilterForm>(FILTER_EDIT_FORM_ID, boxProperties(initialFormValue));

export const initialState = {
  form: initialFormState
};

const initState = (state: State, {payload: filter}: InitFilterForm): State =>
  R.mergeRight(state, {
    form: createFormGroupState<FilterForm>(FILTER_EDIT_FORM_ID, boxProperties(filter))
  });

const resetForm = (form: FormGroupState<FilterForm>) =>
  setValue(reset(form), boxProperties(initialFormValue));

const initStateReducer = createReducer<State, FilterEditActions>([
  createHandler(FilterEditActionTypes.InitFilterForm, initState)
]);

const filterFormReducer = createReducer<State, FilterEditActions>([
  createHandler(FilterEditActionTypes.ResetForm, resetForm)
]);

const validateForm = updateGroup<FilterForm>({
  name: validate([requiredValueValidator])
});

const validateState = (state: State): State =>
  R.evolve({
    form: validateForm
  }, state);

export const reducer = (state = initialState, action: Action): State => {
  const nextState = initStateReducer(state, <FilterEditActions>action);

  const nextFormState = nextState.form;

  const updatedFormState = executeReducers(nextFormState, action, [
    formStateReducer,
    filterFormReducer
  ]);

  return updatedFormState !== nextFormState ?
    validateState({form: updatedFormState})
    : validateState(nextState);
};
